import React, { FC } from "react";
import { Box, Container, IconButton, useDisclosure, Flex } from "@chakra-ui/react";
import { graphql, Link as GatsbyLink, useStaticQuery } from "gatsby";
import { ShiIcon, Spacer } from "src/atoms";
import { Link } from "src/atoms/PrismicLink";
import ShiButton from "src/atoms/ShiButton";
import { MegaMenu } from "./components/MegaMenu";
import { MobileNav } from "./components/MobileNav";
import { MenuData } from "./types";
import { AccountSegmentNavbarLogo } from "../SiteNavbar/types";
export interface GlobalNavigationProps {
  segment: "business" | "accountant";
  isSticky?: boolean;
  accountSegmentNavbarLogo?: AccountSegmentNavbarLogo; // Replace with proper type when GraphQL is available
}

/**
 * GlobalNavigation Component
 * Represents the new Mega Menu architecture for the Business and Accountant segments.
 */
const GlobalNavigation: FC<GlobalNavigationProps> = ({ segment, isSticky, accountSegmentNavbarLogo }) => {
  const data = useStaticQuery<Gatsby.GlobalNavigationQueryQuery>(graphql`
    query GlobalNavigationQuery {
      prismicGlobalMenuBusiness {
        ...GlobalMenuBusinessData
      }
      prismicGlobalMenuAccountant {
        ...GlobalMenuAccountantData
      }
    }
  `);

  const menuData =
    segment === "business"
      ? data.prismicGlobalMenuBusiness?.data
      : data.prismicGlobalMenuAccountant?.data;

  const mobileNav = useDisclosure();

  return (
    <Container
      position={isSticky ? "sticky" : "relative"}
      size="full"
      top={0}
      bg="white"
      zIndex="navbar"
      id="site-navbar"
      h="64px"
      justifyContent="center"
    >
      <Container
        as="nav"
        size="block"
        display="flex"
        alignItems={{ base: "center", navbar: "flex-start" }}
        justifyContent="space-between"
        paddingY={{ base: "space-16", navbar: 0 }}
        paddingX={{ base: "space-16", navbar: "space-58", "2xl": 0 }}
        borderBottom={{ base: "1px solid", navbar: "none" }}
        borderColor="grey-0"
        gridGap={{ base: "space-16", navbar: "0" }}
        h="100%"
      >
        {/* Logo Section */}
        <Box flexShrink={0} marginY="auto" display="flex" alignItems="center">
          <GatsbyLink to="/" style={{ display: "flex", alignItems: "center" }}>
            {segment === "accountant" && accountSegmentNavbarLogo ? (
              <Box
                as="img"
                src={accountSegmentNavbarLogo.url}
                alt={accountSegmentNavbarLogo.alt || "Shine Logo"}
                width={{ base: "96px", navbar: "130px" }}
                height="auto"
              />
            ) : (
              <ShiIcon name="ShineLogo" width={{ base: "96px", navbar: "130px" }} height="auto" />
            )}
          </GatsbyLink>
        </Box>

        <Spacer display={{ base: "block", navbar: "none" }} maxWidth="100%" />

        {/* Mobile/Tablet menu trigger */}
        <IconButton
          display={{ base: "flex", navbar: "none" }}
          aria-label="menu"
          icon={<ShiIcon name="menu" w="20px" h="20px" />}
          onClick={mobileNav.onOpen}
          variant="icon"
          width="32px"
          height="32px"
          minW="unset"
          marginRight="-8px"
        />

        {/* Desktop Menu & CTAs */}
        <Flex
          alignItems="center"
          flexGrow={1}
          display={{ base: "none", navbar: "flex" }}
          h="100%"
        >
          {/* Mega Menu */}
          <Box 
            ml={{ base: "space-24", lg: "space-40", xl: "space-80" }}
            fontSize="font-15"
            flexGrow="1"
            h="100%"
          >
            <MegaMenu body={menuData?.body || []} />
          </Box>
          
          <Spacer />
          
          {/* CTAs */}
          {menuData?.login_cta_label && (
            <Link
              fontWeight="medium"
              variant="link-nav"
              href={menuData.login_cta_link?.url}
              whiteSpace="nowrap"
            >
              {menuData.login_cta_label}
            </Link>
          )}
          {menuData?.start_cta_label && (
            <ShiButton
              as="a"
              href={menuData.start_cta_link?.url}
              variant="signup"
              marginLeft="space-24"
              flexShrink="0"
            >
              {menuData.start_cta_label}
            </ShiButton>
          )}
        </Flex>
      </Container>

      <MobileNav
        menuData={menuData as MenuData | undefined}
        isOpen={mobileNav.isOpen}
        onClose={mobileNav.onClose}
      />
    </Container>
  );
};

export default GlobalNavigation;
